import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  applyScenarioCandidate,
  cancelScenarioSweep,
  createScenarioSweep,
  getScenarioMetrics,
  getScenarioResults,
  getScenarioSweep,
  resumeScenarioSweep,
} from '../../api/api';
import { usePipeline } from '../../context/PipelineContext';
import './ScenarioAnalysis.css';

ChartJS.register(...registerables);

const DEFAULT_METRICS = [
  { id: 'replacement_pct', label: 'Replacement', unit: '%', default_direction: 'maximize' },
  { id: 'capex_crore', label: 'CAPEX', unit: 'Rs crore', default_direction: 'minimize' },
  { id: 'lapsed_pct', label: 'Lapsed energy', unit: '%', default_direction: 'minimize' },
  { id: 'project_irr_pct', label: 'Project IRR', unit: '%', default_direction: 'maximize' },
  { id: 'bess_charging_units', label: 'BESS charging units', unit: 'kWh', default_direction: 'maximize' },
  { id: 'bess_discharging_units', label: 'BESS discharging units', unit: 'kWh', default_direction: 'maximize' },
];

const LEGACY_CHARGE_SLOTS = [9, 10, 11, 12, 13, 14, 15, 16];
const LEGACY_DISCHARGE_SLOTS = [17, 18, 19, 20, 21, 22, 23];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const SLOT_COLORS = ['#0f766e', '#f59e0b', '#7c3aed'];
const SLOT_TINTS = ['rgba(15,118,110,.12)', 'rgba(245,158,11,.14)', 'rgba(124,58,237,.13)'];
const SLOT_LABELS = ['A', 'B', 'C'];

const terminalStatuses = new Set(['completed', 'completed_no_feasible', 'cancelled', 'failed', 'interrupted']);
const sweepStorageKey = (pipelineId) => `scenario:lastSweep:${pipelineId}`;
const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const formatValue = (value, digits = 2) => value === null || value === undefined ? '—' : Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });
const formatDuration = (seconds) => {
  if (!Number.isFinite(Number(seconds))) return 'estimating…';
  const total = Math.max(0, Math.round(Number(seconds)));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  return `${minutes}m ${remainder}s`;
};

/** A labeled slider paired with a synced numeric input — the sidebar's core range control. */
const RangeField = ({ label, value, onChange, min, max, step = 1, suffix }) => {
  const numericValue = number(value, min);
  return (
    <label className="scenario-range-field">
      <div className="scenario-range-head">
        <span>{label}</span>
        <div className="scenario-range-value">
          <input type="number" min="0" step={step} value={value} onChange={(event) => onChange(event.target.value)} />
          {suffix && <small>{suffix}</small>}
        </div>
      </div>
      <input
        type="range"
        className="scenario-slider"
        min={min}
        max={max}
        step={step}
        value={clamp(numericValue, min, max)}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
};

const HourWindow = ({ label, selected, unavailable, onChange }) => {
  const selectedSet = new Set(selected);
  const unavailableSet = new Set(unavailable);
  const toggle = (hour) => {
    if (unavailableSet.has(hour)) return;
    const next = selectedSet.has(hour)
      ? selected.filter((item) => item !== hour)
      : [...selected, hour].sort((a, b) => a - b);
    onChange(next);
  };
  return (
    <div className="scenario-hour-window">
      <span className="scenario-group-label">{label}</span>
      <div className="scenario-hour-rail">
        {HOURS.map((hour) => (
          <button
            key={hour}
            type="button"
            className={`scenario-hour ${selectedSet.has(hour) ? 'selected' : ''}`}
            disabled={unavailableSet.has(hour)}
            onClick={() => toggle(hour)}
          >
            {String(hour).padStart(2, '0')}
          </button>
        ))}
      </div>
    </div>
  );
};

const MetricSelect = ({ metrics, value, onChange, className = '' }) => (
  <select className={`scenario-select ${className}`} value={value} onChange={(event) => onChange(event.target.value)}>
    {metrics.map((metric) => <option key={metric.id} value={metric.id}>{metric.label} · {metric.unit}</option>)}
  </select>
);

/** Collapsible sidebar section shell shared by every control group. */
const Accordion = ({ id, index, title, subtitle, open, onToggle, children }) => (
  <section className={`scenario-accordion ${open ? 'open' : ''}`}>
    <button type="button" className="scenario-accordion-head" onClick={() => onToggle(id)} aria-expanded={open}>
      <span className="scenario-accordion-index">{index}</span>
      <span className="scenario-accordion-titles">
        <strong>{title}</strong>
        {subtitle && <small>{subtitle}</small>}
      </span>
      <svg className="scenario-accordion-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
    {open && <div className="scenario-accordion-body">{children}</div>}
  </section>
);

const ScenarioAnalysis = ({ onClose }) => {
  const { session } = usePipeline();
  const [metrics, setMetrics] = React.useState(DEFAULT_METRICS);
  const [windMode, setWindMode] = React.useState('turbine_count');
  const [wind, setWind] = React.useState({ minimum: 1, maximum: 5, step: 1, mwPerTurbine: 2.1 });
  const [solar, setSolar] = React.useState({ minimum: 1, maximum: 10, step: 1 });
  const [sweepBatteryCapacity, setSweepBatteryCapacity] = React.useState(false);
  const [batteryCapacity, setBatteryCapacity] = React.useState({ minimum: 500, maximum: 2000, step: 500 });
  const [sweepBatteryHours, setSweepBatteryHours] = React.useState(false);
  const [batteryHours, setBatteryHours] = React.useState({ minimum: 2, maximum: 4, step: 1 });
  const [chargeSlots, setChargeSlots] = React.useState(LEGACY_CHARGE_SLOTS);
  const [dischargeSlots, setDischargeSlots] = React.useState(LEGACY_DISCHARGE_SLOTS);
  const [capacitySlice, setCapacitySlice] = React.useState('');
  const [hoursSlice, setHoursSlice] = React.useState('');
  const [objectives, setObjectives] = React.useState([{ metric_id: 'replacement_pct', direction: 'maximize' }]);
  const [constraints, setConstraints] = React.useState([]);
  const [metricOptions, setMetricOptions] = React.useState({ replacement_basis: 'post_banking', lapsed_basis: 'post_banking', lapsed_denominator: 'generation' });
  const [sweep, setSweep] = React.useState(null);
  const [results, setResults] = React.useState([]);
  const [heatmapMetric, setHeatmapMetric] = React.useState('replacement_pct');
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [chartMetricIds, setChartMetricIds] = React.useState(['replacement_pct', 'capex_crore', 'lapsed_pct']);
  const [chartType, setChartType] = React.useState('bar');
  const comparisonChartRef = React.useRef(null);
  const [normalizeChart, setNormalizeChart] = React.useState(false);
  const [openSection, setOpenSection] = React.useState('envelope');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    getScenarioMetrics()
      .then((data) => data?.metrics?.length && setMetrics(data.metrics))
      .catch(() => {});
  }, []);

  // Rehydrate the last sweep run for this pipeline — sweeps live server-side,
  // so closing the tab (or navigating away and back) shouldn't lose them.
  // Component state alone can't survive an unmount, so the sweep id is kept
  // in localStorage and the results are re-fetched by id on mount.
  React.useEffect(() => {
    if (!session.pipeline_id) return undefined;
    const storedId = window.localStorage.getItem(sweepStorageKey(session.pipeline_id));
    if (!storedId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const current = await getScenarioSweep(storedId);
        if (cancelled) return;
        setSweep(current);
        const data = await getScenarioResults(current.id);
        if (cancelled) return;
        setResults(data.candidates || []);
      } catch (requestError) {
        window.localStorage.removeItem(sweepStorageKey(session.pipeline_id));
      }
    })();
    return () => { cancelled = true; };
  }, [session.pipeline_id]);

  React.useEffect(() => {
    if (!sweep?.id || terminalStatuses.has(sweep.status)) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        const current = await getScenarioSweep(sweep.id);
        setSweep(current);
        const data = await getScenarioResults(current.id);
        setResults(data.candidates || []);
        setSelectedIds((existing) => existing.length ? existing : (data.candidates?.[0] ? [data.candidates[0].candidate.candidate_id] : []));
      } catch (requestError) {
        setError(requestError.message || 'Could not refresh the sweep');
      }
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [sweep]);

  const metricById = React.useMemo(() => Object.fromEntries(metrics.map((metric) => [metric.id, metric])), [metrics]);
  const batteryParams = React.useMemo(() => {
    const node = (session.nodes || []).find((item) => item.node_type === 'battery');
    return Array.isArray(node?.node_params) ? (node.node_params[0] || null) : null;
  }, [session.nodes]);
  const hasBattery = Boolean(batteryParams);
  const batteryPrefillKey = batteryParams
    ? [
      batteryParams.battery_capacity_kw,
      batteryParams.battery_hours,
      (batteryParams.charge_time_slots || []).join(','),
      (batteryParams.discharge_time_slots || []).join(','),
    ].join('|')
    : '';
  const batteryPrefillRef = React.useRef('');
  React.useEffect(() => {
    if (!batteryParams || batteryPrefillRef.current === batteryPrefillKey) return;
    batteryPrefillRef.current = batteryPrefillKey;
    const capacity = number(batteryParams.battery_capacity_kw, 0);
    const hours = number(batteryParams.battery_hours, 2) || 2;
    if (capacity > 0) {
      setBatteryCapacity((current) => ({
        ...current,
        minimum: capacity,
        maximum: Math.max(number(current.maximum, capacity), capacity),
      }));
    }
    setBatteryHours((current) => ({
      ...current,
      minimum: hours,
      maximum: Math.max(number(current.maximum, hours), hours),
    }));
    const charge = Array.isArray(batteryParams.charge_time_slots)
      ? batteryParams.charge_time_slots.map(Number).filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23)
      : [];
    const discharge = Array.isArray(batteryParams.discharge_time_slots)
      ? batteryParams.discharge_time_slots.map(Number).filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23)
      : [];
    if (charge.length) setChargeSlots(charge);
    if (discharge.length) setDischargeSlots(discharge);
  }, [batteryParams, batteryPrefillKey]);
  const axisCount = (axis, enabled) => (
    enabled
      ? Math.max(0, Math.floor((number(axis.maximum) - number(axis.minimum)) / Math.max(number(axis.step, 1), 0.000001)) + 1)
      : 1
  );
  const windCount = Math.max(0, Math.floor((number(wind.maximum) - number(wind.minimum)) / Math.max(number(wind.step, 1), 0.000001)) + 1);
  const solarCount = Math.max(0, Math.floor((number(solar.maximum) - number(solar.minimum)) / Math.max(number(solar.step, 1), 0.000001)) + 1);
  const candidateCount = windCount * solarCount * axisCount(batteryCapacity, sweepBatteryCapacity) * axisCount(batteryHours, sweepBatteryHours);
  const formatBess = (candidate) => {
    if (!candidate || (!number(candidate.battery_capacity_kw) && !number(candidate.battery_hours))) return '';
    return `${formatValue(candidate.battery_capacity_kw, 0)} kW / ${formatValue(candidate.battery_hours, 1)} h`;
  };
  const completedResults = results.filter((row) => row.status === 'completed');
  const slots = selectedIds.map((id) => results.find((row) => row.candidate.candidate_id === id)).filter(Boolean);

  const updateObjective = (index, patch) => setObjectives((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  const updateConstraint = (index, patch) => setConstraints((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));

  const toggleSlot = (candidateId) => {
    setSelectedIds((current) => {
      if (current.includes(candidateId)) return current.filter((id) => id !== candidateId);
      if (current.length < 3) return [...current, candidateId];
      return [...current.slice(1), candidateId];
    });
  };
  const removeSlot = (candidateId) => setSelectedIds((current) => current.filter((id) => id !== candidateId));
  const toggleChartMetric = (metricId) => setChartMetricIds((current) => current.includes(metricId) ? current.filter((id) => id !== metricId) : [...current, metricId]);

  const startSweep = async () => {
    if (!session.pipeline_id) {
      setError('Save or open a pipeline before starting an optimization sweep.');
      return;
    }
    if (!objectives.length) {
      setError('Add at least one objective.');
      return;
    }
    if (hasBattery && (!chargeSlots.length || !dischargeSlots.length)) {
      setError('Select at least one charging hour and one discharging hour.');
      return;
    }
    if (hasBattery && chargeSlots.some((hour) => dischargeSlots.includes(hour))) {
      setError('Charging and discharging hours cannot overlap.');
      return;
    }
    if (candidateCount > 250 && !window.confirm(`This sweep contains ${candidateCount} full pipeline runs. Continue?`)) return;
    setBusy(true);
    setError('');
    setResults([]);
    setSelectedIds([]);
    try {
      const created = await createScenarioSweep({
        pipeline_id: session.pipeline_id,
        name: `${session.pipeline_name} optimization`,
        grid: {
          wind: {
            mode: windMode,
            range: { minimum: number(wind.minimum), maximum: number(wind.maximum), step: number(wind.step) },
            mw_per_turbine: number(wind.mwPerTurbine),
          },
          solar_mw: { minimum: number(solar.minimum), maximum: number(solar.maximum), step: number(solar.step) },
          ...(hasBattery && sweepBatteryCapacity ? {
            battery_capacity_kw: {
              minimum: number(batteryCapacity.minimum),
              maximum: number(batteryCapacity.maximum),
              step: number(batteryCapacity.step),
            },
          } : {}),
          ...(hasBattery && sweepBatteryHours ? {
            battery_hours: {
              minimum: number(batteryHours.minimum),
              maximum: number(batteryHours.maximum),
              step: number(batteryHours.step),
            },
          } : {}),
          ...(hasBattery ? {
            charge_time_slots: chargeSlots,
            discharge_time_slots: dischargeSlots,
          } : {}),
          max_candidates: 250,
          confirm_large_sweep: candidateCount > 250,
        },
        objectives: objectives.map((objective, index) => ({ ...objective, priority: index + 1 })),
        constraints: constraints.map((constraint) => ({ ...constraint, value: number(constraint.value) })),
        display_metrics: metrics.map((metric) => metric.id),
        metric_options: metricOptions,
      });
      setSweep(created);
      window.localStorage.setItem(sweepStorageKey(session.pipeline_id), created.id);
    } catch (requestError) {
      setError(requestError.message || 'Could not start the scenario sweep');
    } finally {
      setBusy(false);
    }
  };

  const cancelOrResume = async () => {
    if (!sweep?.id) return;
    setBusy(true);
    try {
      const updated = sweep.status === 'cancelled' || sweep.status === 'interrupted' || sweep.status === 'failed'
        ? await resumeScenarioSweep(sweep.id)
        : await cancelScenarioSweep(sweep.id);
      setSweep(updated);
    } catch (requestError) {
      setError(requestError.message || 'Could not update the sweep');
    } finally {
      setBusy(false);
    }
  };

  const applyCandidate = async (row) => {
    if (!row) return;
    const bess = formatBess(row.candidate);
    const windows = (sweep?.configuration?.grid?.charge_time_slots || []).length
      ? ` Charge ${sweep.configuration.grid.charge_time_slots.join(', ')}; discharge ${ (sweep.configuration.grid.discharge_time_slots || []).join(', ') }.`
      : '';
    const message = `Apply ${row.candidate.wind_turbines} turbines and ${row.candidate.solar_mw} MWp solar${bess ? `, ${bess} battery` : ''} to ${session.pipeline_name}?${windows}`;
    if (!window.confirm(message)) return;
    setBusy(true);
    try {
      await applyScenarioCandidate(sweep.id, row.candidate.candidate_id);
      setSweep((current) => ({ ...current, selected_candidate_id: row.candidate.candidate_id }));
      window.dispatchEvent(new CustomEvent('pipeline:global-params-updated', { detail: { pipelineName: session.pipeline_name, source: 'scenario-analysis' } }));
    } catch (requestError) {
      setError(requestError.message || 'Could not apply the combination');
    } finally {
      setBusy(false);
    }
  };

  const exportWorkbook = () => {
    const rows = results.map((row) => ({
      Candidate: row.candidate.candidate_id,
      'Wind turbines': row.candidate.wind_turbines,
      'Requested wind MW': row.candidate.requested_wind_mw,
      'Actual wind MW': row.candidate.actual_wind_mw,
      'Solar MWp': row.candidate.solar_mw,
      'Battery capacity kW': row.candidate.battery_capacity_kw,
      'Battery hours': row.candidate.battery_hours,
      Feasible: row.feasible ? 'Yes' : 'No',
      Pareto: row.pareto ? 'Yes' : 'No',
      Rank: row.rank,
      Status: row.status,
      Violations: (row.violations || []).map((item) => item.message).join('; '),
      ...Object.fromEntries(metrics.map((metric) => [`${metric.label} (${metric.unit})`, row.metrics?.[metric.id]])),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Candidates');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([
      { key: 'Assumption hash', value: sweep.assumption_hash },
      { key: 'Objectives', value: objectives.map((item) => `${item.metric_id}:${item.direction}`).join(', ') },
      { key: 'Constraints', value: constraints.map((item) => `${item.metric_id} ${item.operator} ${item.value}`).join(', ') },
      ...Object.entries(metricOptions).map(([key, value]) => ({ key, value })),
    ]), 'Audit');
    XLSX.writeFile(workbook, `${session.pipeline_name || 'scenario'}-optimization.xlsx`);
  };

  // ---- Heat map: a real wind × solar matrix with axes + a legend scale ----
  const uniqueSorted = (values) => Array.from(new Set(values.filter((value) => value !== undefined && value !== null))).sort((a, b) => a - b);
  const windRows = React.useMemo(() => uniqueSorted(results.map((row) => row.candidate.wind_turbines)), [results]);
  const solarCols = React.useMemo(() => uniqueSorted(results.map((row) => row.candidate.solar_mw)), [results]);
  const capacityOptions = React.useMemo(() => uniqueSorted(results.map((row) => row.candidate.battery_capacity_kw)), [results]);
  const hoursOptions = React.useMemo(() => uniqueSorted(results.map((row) => row.candidate.battery_hours)), [results]);
  const activeCapacity = capacityOptions.find((value) => String(value) === String(capacitySlice)) ?? capacityOptions[0];
  const activeHours = hoursOptions.find((value) => String(value) === String(hoursSlice)) ?? hoursOptions[0];
  const sameNumber = (left, right) => Number(left) === Number(right);
  const matchesSlice = (row) => (
    (capacityOptions.length <= 1 || sameNumber(row.candidate.battery_capacity_kw, activeCapacity))
    && (hoursOptions.length <= 1 || sameNumber(row.candidate.battery_hours, activeHours))
  );
  const slicedResults = results.filter(matchesSlice);
  const heatValues = slicedResults
    .filter((row) => row.status === 'completed')
    .map((row) => row.metrics?.[heatmapMetric])
    .filter((value) => Number.isFinite(value));
  const heatLow = heatValues.length ? Math.min(...heatValues) : 0;
  const heatHigh = heatValues.length ? Math.max(...heatValues) : 1;
  const heatDirection = metricById[heatmapMetric]?.default_direction === 'minimize' ? 'minimize' : 'maximize';
  const scoreFor = (value) => {
    if (!Number.isFinite(value)) return null;
    let score = (value - heatLow) / Math.max(heatHigh - heatLow, 0.000001);
    if (heatDirection === 'minimize') score = 1 - score;
    return clamp(score, 0, 1);
  };
  // red (bad) -> amber -> green (good)
  const heatColor = (score) => score === null ? 'transparent' : `hsl(${score * 122} 68% ${88 - score * 36}%)`;
  const cellFor = (windValue, solarValue) => slicedResults.find((row) => row.candidate.wind_turbines === windValue && row.candidate.solar_mw === solarValue);

  // ---- Comparison chart across up to 3 selected combinations ----
  const chartMetrics = metrics.filter((metric) => chartMetricIds.includes(metric.id));
  const normalizedValue = (metricId, value) => {
    if (!Number.isFinite(value)) return null;
    const values = completedResults.map((row) => row.metrics?.[metricId]).filter(Number.isFinite);
    if (!values.length) return null;
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    return ((value - lo) / Math.max(hi - lo, 0.000001)) * 100;
  };
  const comparisonData = {
    labels: chartMetrics.map((metric) => normalizeChart ? metric.label : `${metric.label} (${metric.unit})`),
    datasets: slots.map((row, index) => ({
      label: row.candidate.candidate_id,
      data: chartMetrics.map((metric) => {
        const raw = row.metrics?.[metric.id];
        return normalizeChart ? normalizedValue(metric.id, raw) : raw;
      }),
      backgroundColor: chartType === 'bar' ? `${SLOT_COLORS[index]}CC` : 'transparent',
      borderColor: SLOT_COLORS[index],
      borderWidth: 2,
      pointBackgroundColor: SLOT_COLORS[index],
      pointRadius: 4,
      tension: 0.25,
    })),
  };

  const comparisonColumns = slots.map((row, index) => ({
    id: row.candidate.candidate_id,
    label: `${SLOT_LABELS[index]} · ${row.candidate.wind_turbines} WTG / ${row.candidate.solar_mw} MWp${formatBess(row.candidate) ? ` / ${formatBess(row.candidate)}` : ''}`,
  }));

  const comparisonTableRows = metrics.map((metric) => {
    const values = slots.map((row) => row.metrics?.[metric.id]);
    const finite = values.filter(Number.isFinite);
    const best = finite.length ? (metric.default_direction === 'minimize' ? Math.min(...finite) : Math.max(...finite)) : null;
    return { metric, values, best, highlight: finite.length > 1 };
  });

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportComparison = (kind) => {
    if (slots.length < 2) return;
    const chart = comparisonChartRef.current;
    const chartImage = chart?.toBase64Image?.() || '';
    const pipelineName = session.pipeline_name || 'scenario';
    const safeName = String(pipelineName).replace(/[^a-z0-9._-]+/gi, '_') || 'scenario';
    const chartNote = `${chartType === 'line' ? 'Line' : 'Bar'} chart${normalizeChart ? ', normalized to %' : ''}`;

    if (kind === 'html') {
      const head = comparisonColumns.map((column) => `<th>${column.label}</th>`).join('');
      const body = comparisonTableRows.map(({ metric, values, best, highlight }) => {
        const cells = values.map((value) => {
          const isBest = highlight && Number.isFinite(value) && value === best;
          return `<td${isBest ? ' class="best"' : ''}>${formatValue(value)}</td>`;
        }).join('');
        return `<tr><td>${metric.label}<small>${metric.unit}</small></td>${cells}</tr>`;
      }).join('');
      const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${pipelineName} scenario comparison</title>
<style>
body{font-family:Segoe UI,sans-serif;color:#111;margin:32px;}
h1{font-size:22px;margin:0 0 4px;} p{color:#444;margin:0 0 16px;}
img{max-width:100%;height:auto;margin:8px 0 20px;}
table{border-collapse:collapse;width:100%;} th,td{border:1px solid #d1d5db;padding:8px 10px;text-align:right;}
th:first-child,td:first-child{text-align:left;} small{display:block;color:#6b7280;font-weight:400;}
.best{background:#ecfdf5;color:#047857;font-weight:700;}
</style></head><body>
<h1>Metric-by-metric delta</h1>
<p>${pipelineName} · ${chartNote}</p>
${chartImage ? `<img alt="${chartNote}" src="${chartImage}" />` : '<p>No comparison chart was available.</p>'}
<table><thead><tr><th>Metric</th>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
      downloadBlob(new Blob([html], { type: 'text/html' }), `${safeName}-scenario-comparison.html`);
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFontSize(16);
    doc.text('Metric-by-metric delta', 40, 40);
    doc.setFontSize(10);
    doc.text(`${pipelineName} · ${chartNote}`, 40, 58);
    let tableStart = 78;
    if (chartImage) {
      const width = 520;
      const height = 220;
      doc.addImage(chartImage, 'PNG', 40, 72, width, height);
      tableStart = 72 + height + 16;
    }
    autoTable(doc, {
      startY: tableStart,
      head: [['Metric', ...comparisonColumns.map((column) => column.label)]],
      body: comparisonTableRows.map(({ metric, values }) => [
        `${metric.label} (${metric.unit})`,
        ...values.map((value) => formatValue(value)),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [15, 118, 110] },
      didParseCell: (hook) => {
        if (hook.section !== 'body' || hook.column.index === 0) return;
        const row = comparisonTableRows[hook.row.index];
        const value = row?.values?.[hook.column.index - 1];
        if (row?.highlight && Number.isFinite(value) && value === row.best) {
          hook.cell.styles.fillColor = [236, 253, 245];
          hook.cell.styles.textColor = [4, 120, 87];
          hook.cell.styles.fontStyle = 'bold';
        }
      },
    });
    doc.save(`${safeName}-scenario-comparison.pdf`);
  };

  const ACCORDION_SECTIONS = [
    { id: 'envelope', index: '01', title: 'Capacity envelope', subtitle: `${candidateCount} combinations · ≈ ${candidateCount} runs` },
    { id: 'rules', index: '02', title: 'Decision rules', subtitle: 'Objectives are evaluated in order' },
    { id: 'basis', index: '03', title: 'Metric basis', subtitle: 'Replacement & lapsed conventions' },
  ];

  return (
    <main className="scenario-shell">
      <header className="scenario-topbar">
        <div>
          <p>Decision grid · {session.pipeline_name}</p>
          <h1>Scenario optimization</h1>
          <span>Freeze assumptions, sweep capacity, keep only combinations that satisfy the brief.</span>
        </div>
        <button className="scenario-ghost" onClick={onClose}>Back to pipeline</button>
      </header>

      {error && <div className="scenario-error">{error}<button onClick={() => setError('')}>×</button></div>}

      <section className="scenario-layout">
        <aside className="scenario-control-rail">
          {ACCORDION_SECTIONS.map((section) => (
            <Accordion key={section.id} id={section.id} index={section.index} title={section.title} subtitle={section.subtitle} open={openSection === section.id} onToggle={(id) => setOpenSection((current) => current === id ? '' : id)}>
              {section.id === 'envelope' && (
                <>
                  <div className="scenario-segmented">
                    <button className={windMode === 'turbine_count' ? 'active' : ''} onClick={() => setWindMode('turbine_count')}>Turbines</button>
                    <button className={windMode === 'mw' ? 'active' : ''} onClick={() => setWindMode('mw')}>Requested MW</button>
                  </div>
                  <div className="scenario-range-group">
                    <span className="scenario-group-label">Wind</span>
                    <RangeField label="From" value={wind.minimum} onChange={(value) => setWind({ ...wind, minimum: value })} min={0} max={windMode === 'mw' ? 200 : 50} suffix={windMode === 'mw' ? 'MW' : 'WTG'} />
                    <RangeField label="To" value={wind.maximum} onChange={(value) => setWind({ ...wind, maximum: value })} min={0} max={windMode === 'mw' ? 200 : 50} suffix={windMode === 'mw' ? 'MW' : 'WTG'} />
                    <RangeField label="Step" value={wind.step} onChange={(value) => setWind({ ...wind, step: value })} min={windMode === 'mw' ? 0.5 : 1} max={windMode === 'mw' ? 20 : 10} step={windMode === 'mw' ? 0.5 : 1} suffix={windMode === 'mw' ? 'MW' : 'WTG'} />
                    <RangeField label="MW / turbine" value={wind.mwPerTurbine} onChange={(value) => setWind({ ...wind, mwPerTurbine: value })} min={0.5} max={10} step={0.1} suffix="MW" />
                  </div>
                  <div className="scenario-range-group">
                    <span className="scenario-group-label">Solar</span>
                    <RangeField label="From" value={solar.minimum} onChange={(value) => setSolar({ ...solar, minimum: value })} min={0} max={300} suffix="MWp" />
                    <RangeField label="To" value={solar.maximum} onChange={(value) => setSolar({ ...solar, maximum: value })} min={0} max={300} suffix="MWp" />
                    <RangeField label="Step" value={solar.step} onChange={(value) => setSolar({ ...solar, step: value })} min={0.5} max={20} step={0.5} suffix="MWp" />
                  </div>
                  <div className="scenario-range-group">
                    <span className="scenario-group-label">BESS</span>
                    {hasBattery ? (
                      <>
                        <label className="scenario-normalize">
                          <input type="checkbox" checked={sweepBatteryCapacity} onChange={(event) => setSweepBatteryCapacity(event.target.checked)} />
                          Sweep battery capacity
                        </label>
                        {sweepBatteryCapacity && (
                          <>
                            <RangeField label="From" value={batteryCapacity.minimum} onChange={(value) => setBatteryCapacity({ ...batteryCapacity, minimum: value })} min={1} max={20000} step={50} suffix="kW" />
                            <RangeField label="To" value={batteryCapacity.maximum} onChange={(value) => setBatteryCapacity({ ...batteryCapacity, maximum: value })} min={1} max={20000} step={50} suffix="kW" />
                            <RangeField label="Step" value={batteryCapacity.step} onChange={(value) => setBatteryCapacity({ ...batteryCapacity, step: value })} min={1} max={5000} step={1} suffix="kW" />
                          </>
                        )}
                        <label className="scenario-normalize">
                          <input type="checkbox" checked={sweepBatteryHours} onChange={(event) => setSweepBatteryHours(event.target.checked)} />
                          Sweep battery hours
                        </label>
                        {sweepBatteryHours && (
                          <>
                            <RangeField label="From" value={batteryHours.minimum} onChange={(value) => setBatteryHours({ ...batteryHours, minimum: value })} min={1} max={12} step={0.5} suffix="h" />
                            <RangeField label="To" value={batteryHours.maximum} onChange={(value) => setBatteryHours({ ...batteryHours, maximum: value })} min={1} max={12} step={0.5} suffix="h" />
                            <RangeField label="Step" value={batteryHours.step} onChange={(value) => setBatteryHours({ ...batteryHours, step: value })} min={0.5} max={4} step={0.5} suffix="h" />
                          </>
                        )}
                        <HourWindow label="Charging window" selected={chargeSlots} unavailable={dischargeSlots} onChange={setChargeSlots} />
                        <HourWindow label="Discharging window" selected={dischargeSlots} unavailable={chargeSlots} onChange={setDischargeSlots} />
                      </>
                    ) : (
                      <p className="scenario-bess-note">Add a battery node to sweep capacity, hours, and charge or discharge windows.</p>
                    )}
                  </div>
                </>
              )}

              {section.id === 'rules' && (
                <div className="scenario-rules">
                  {objectives.map((objective, index) => (
                    <div className="scenario-rule" key={`${objective.metric_id}-${index}`}>
                      <div className="scenario-rule-head"><b>Objective {index + 1}</b>{objectives.length > 1 && <button className="scenario-rule-remove" onClick={() => setObjectives((items) => items.filter((_, itemIndex) => itemIndex !== index))} aria-label="Remove objective">×</button>}</div>
                      <div className="scenario-rule-controls">
                        <MetricSelect metrics={metrics} value={objective.metric_id} onChange={(metric_id) => updateObjective(index, { metric_id, direction: metricById[metric_id]?.default_direction || 'minimize' })} />
                        <select className="scenario-select" value={objective.direction} onChange={(event) => updateObjective(index, { direction: event.target.value })}><option value="maximize">Maximize</option><option value="minimize">Minimize</option></select>
                      </div>
                    </div>
                  ))}
                  <button className="scenario-add" onClick={() => setObjectives((items) => [...items, { metric_id: 'capex_crore', direction: 'minimize' }])}>+ Add objective</button>
                  {constraints.map((constraint, index) => (
                    <div className="scenario-rule constraint" key={`${constraint.metric_id}-${index}`}>
                      <div className="scenario-rule-head"><b>Hard constraint</b><button className="scenario-rule-remove" onClick={() => setConstraints((items) => items.filter((_, itemIndex) => itemIndex !== index))} aria-label="Remove constraint">×</button></div>
                      <div className="scenario-constraint-fields">
                        <MetricSelect metrics={metrics} value={constraint.metric_id} onChange={(metric_id) => updateConstraint(index, { metric_id })} />
                        <div className="scenario-inline">
                          <select className="scenario-select" value={constraint.operator} onChange={(event) => updateConstraint(index, { operator: event.target.value })}><option value="lte">≤</option><option value="gte">≥</option><option value="lt">&lt;</option><option value="gt">&gt;</option><option value="eq">=</option></select>
                          <input type="number" value={constraint.value} onChange={(event) => updateConstraint(index, { value: event.target.value })} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button className="scenario-add" onClick={() => setConstraints((items) => [...items, { metric_id: 'capex_crore', operator: 'lte', value: 100 }])}>+ Add constraint</button>
                </div>
              )}

              {section.id === 'basis' && (
                <div className="scenario-basis">
                  <label>Replacement basis<select className="scenario-select" value={metricOptions.replacement_basis} onChange={(event) => setMetricOptions({ ...metricOptions, replacement_basis: event.target.value })}><option value="post_banking">Post-banking</option><option value="pre_banking">Pre-banking</option></select></label>
                  <label>Lapsed basis<select className="scenario-select" value={metricOptions.lapsed_basis} onChange={(event) => setMetricOptions({ ...metricOptions, lapsed_basis: event.target.value })}><option value="post_banking">Post-banking</option><option value="pre_banking">Pre-banking</option></select></label>
                  <label>Lapsed denominator<select className="scenario-select" value={metricOptions.lapsed_denominator} onChange={(event) => setMetricOptions({ ...metricOptions, lapsed_denominator: event.target.value })}><option value="generation">Generation</option><option value="consumption">Consumption</option></select></label>
                </div>
              )}
            </Accordion>
          ))}

          <button className="scenario-run" disabled={busy || (sweep && !terminalStatuses.has(sweep.status))} onClick={startSweep}>{busy ? 'Working…' : 'Run decision grid'}</button>
        </aside>

        <div className="scenario-results">
          {sweep && (
            <section className="scenario-progress">
              <div>
                <span className={`scenario-status ${sweep.status}`}>{sweep.status.replaceAll('_', ' ')}</span>
                <strong>{sweep.progress?.completed || 0} / {sweep.progress?.total || 0}</strong>
                <small>
                  {sweep.progress?.active_workers || 0}/{sweep.progress?.worker_limit || 1} workers · ETA {formatDuration(sweep.progress?.eta_seconds)} · {sweep.progress?.failed || 0} failed
                </small>
              </div>
              <progress max={sweep.progress?.total || 1} value={(sweep.progress?.completed || 0) + (sweep.progress?.failed || 0)} />
              <button className="scenario-ghost" disabled={busy || sweep.status === 'completed' || sweep.status === 'completed_no_feasible'} onClick={cancelOrResume}>{['cancelled', 'interrupted', 'failed'].includes(sweep.status) ? 'Resume' : 'Cancel'}</button>
            </section>
          )}

          {!results.length ? (
            <section className="scenario-empty">
              <div className="scenario-grid-mark" aria-hidden="true">{Array.from({ length: 30 }).map((_, index) => <i key={index} />)}</div>
              <h2>Run scenarios for your model</h2>
              <p>Each cell will become one isolated pipeline run. Pick up to three combinations from the heat map or the table to compare them side by side.</p>
            </section>
          ) : (
            <>
              {!!slots.length && (
                <section className="scenario-tray">
                  {selectedIds.map((id, index) => {
                    const row = results.find((candidate) => candidate.candidate.candidate_id === id);
                    if (!row) return null;
                    return (
                      <div className="scenario-tray-chip" key={id} style={{ borderColor: SLOT_COLORS[index], background: SLOT_TINTS[index] }}>
                        <span className="scenario-tray-dot" style={{ background: SLOT_COLORS[index] }}>{SLOT_LABELS[index]}</span>
                        <div className="scenario-tray-info">
                          <strong>{row.candidate.wind_turbines} WTG · {row.candidate.solar_mw} MWp{formatBess(row.candidate) ? ` · ${formatBess(row.candidate)}` : ''}</strong>
                          <small>{formatValue(row.metrics?.[heatmapMetric])} {metricById[heatmapMetric]?.unit}</small>
                        </div>
                        <button className="scenario-tray-apply" onClick={() => applyCandidate(row)} disabled={busy} title="Apply this combination to the pipeline">Apply</button>
                        <button className="scenario-tray-remove" onClick={() => removeSlot(id)} aria-label="Remove from comparison">×</button>
                      </div>
                    );
                  })}
                  {selectedIds.length < 3 && <div className="scenario-tray-hint">Select {3 - selectedIds.length} more from the heat map or table to compare</div>}
                </section>
              )}

              <section className="scenario-matrix-card">
                <div className="scenario-card-head">
                  <div><small>Decision surface</small><h2>Wind × solar heat map</h2></div>
                  <div className="scenario-slice-controls">
                    {capacityOptions.length > 1 && (
                      <label>Capacity
                        <select className="scenario-select" value={String(activeCapacity ?? '')} onChange={(event) => setCapacitySlice(event.target.value)}>
                          {capacityOptions.map((value) => <option key={value} value={value}>{formatValue(value, 0)} kW</option>)}
                        </select>
                      </label>
                    )}
                    {hoursOptions.length > 1 && (
                      <label>Hours
                        <select className="scenario-select" value={String(activeHours ?? '')} onChange={(event) => setHoursSlice(event.target.value)}>
                          {hoursOptions.map((value) => <option key={value} value={value}>{formatValue(value, 1)} h</option>)}
                        </select>
                      </label>
                    )}
                    <MetricSelect metrics={metrics} value={heatmapMetric} onChange={setHeatmapMetric} />
                  </div>
                </div>
                <div className="scenario-heatmap-scroll">
                  <div className="scenario-heatmap" style={{ gridTemplateColumns: `88px repeat(${solarCols.length}, minmax(72px, 1fr))` }}>
                    <div className="scenario-heatmap-corner">
                      <span>Wind ↓</span>
                      <span>Solar →</span>
                    </div>
                    {solarCols.map((solarValue) => <div className="scenario-heatmap-colhead" key={`col-${solarValue}`}>{solarValue}<small>MWp</small></div>)}
                    {windRows.map((windValue) => (
                      <React.Fragment key={`row-${windValue}`}>
                        <div className="scenario-heatmap-rowhead">{windValue}<small>WTG</small></div>
                        {solarCols.map((solarValue) => {
                          const row = cellFor(windValue, solarValue);
                          if (!row) return <div className="scenario-heatmap-cell empty" key={`${windValue}-${solarValue}`} />;
                          const value = row.metrics?.[heatmapMetric];
                          const score = row.status === 'failed' ? null : scoreFor(value);
                          const slotIndex = selectedIds.indexOf(row.candidate.candidate_id);
                          return (
                            <button
                              key={`${windValue}-${solarValue}`}
                              className={`scenario-heatmap-cell ${row.feasible ? 'feasible' : 'infeasible'} ${row.pareto ? 'pareto' : ''} ${row.status === 'failed' ? 'failed' : ''} ${slotIndex >= 0 ? 'in-tray' : ''}`}
                              style={{ background: row.status === 'failed' ? 'repeating-linear-gradient(45deg, #fecaca, #fecaca 4px, #fee2e2 4px, #fee2e2 8px)' : heatColor(score), outlineColor: slotIndex >= 0 ? SLOT_COLORS[slotIndex] : 'transparent' }}
                              onClick={() => toggleSlot(row.candidate.candidate_id)}
                              title={`${windValue} WTG · ${solarValue} MWp${formatBess(row.candidate) ? ` · ${formatBess(row.candidate)}` : ''} · ${formatValue(value)} ${metricById[heatmapMetric]?.unit || ''}`}
                            >
                              {slotIndex >= 0 && <i className="scenario-heatmap-badge" style={{ background: SLOT_COLORS[slotIndex] }}>{SLOT_LABELS[slotIndex]}</i>}
                              {row.pareto && <i className="scenario-heatmap-pareto">P</i>}
                              <span>{formatValue(value, 1)}</span>
                            </button>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div className="scenario-heatmap-legend">
                  <span>{heatDirection === 'minimize' ? 'Better (low)' : 'Worse (low)'}</span>
                  <div className="scenario-legend-bar" />
                  <span>{heatDirection === 'minimize' ? 'Worse (high)' : 'Better (high)'}</span>
                  <span className="scenario-legend-range">{formatValue(heatLow, 1)} – {formatValue(heatHigh, 1)} {metricById[heatmapMetric]?.unit}</span>
                </div>
              </section>

              <section className="scenario-chart-grid">
                <article className="scenario-compare-card">
                  <div className="scenario-card-head">
                    <div><small>Side-by-side</small><h3>Compare selected combinations</h3></div>
                    <div className="scenario-compare-controls">
                      <div className="scenario-segmented small">
                        <button className={chartType === 'bar' ? 'active' : ''} onClick={() => setChartType('bar')}>Bar</button>
                        <button className={chartType === 'line' ? 'active' : ''} onClick={() => setChartType('line')}>Line</button>
                      </div>
                      <label className="scenario-normalize"><input type="checkbox" checked={normalizeChart} onChange={(event) => setNormalizeChart(event.target.checked)} />Normalize %</label>
                    </div>
                  </div>
                  <div className="scenario-metric-chips">
                    {metrics.map((metric) => (
                      <button key={metric.id} className={`scenario-chip ${chartMetricIds.includes(metric.id) ? 'active' : ''}`} onClick={() => toggleChartMetric(metric.id)}>{metric.label}</button>
                    ))}
                  </div>
                  {!slots.length ? (
                    <div className="scenario-chart-placeholder">Select up to 3 combinations from the heat map or table below to compare them here.</div>
                  ) : !chartMetrics.length ? (
                    <div className="scenario-chart-placeholder">Choose at least one metric above.</div>
                  ) : chartType === 'bar' ? (
                    <Bar ref={comparisonChartRef} data={comparisonData} options={{ responsive: true, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: normalizeChart } } }} />
                  ) : (
                    <Line ref={comparisonChartRef} data={comparisonData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
                  )}
                </article>
              </section>

              {slots.length > 1 && (
                <section className="scenario-pin-card">
                  <div className="scenario-card-head">
                    <div><small>Pinned comparison</small><h2>Metric-by-metric delta</h2></div>
                    <div className="scenario-actions">
                      <button className="scenario-ghost" onClick={() => exportComparison('html')}>Export HTML</button>
                      <button className="scenario-ghost" onClick={() => exportComparison('pdf')}>Export PDF</button>
                    </div>
                  </div>
                  <div className="scenario-table-scroll">
                    <table className="scenario-pin-table">
                      <thead>
                        <tr>
                          <th>Metric</th>
                          {slots.map((row, index) => (
                            <th key={row.candidate.candidate_id}><span className="scenario-pin-dot" style={{ background: SLOT_COLORS[index] }}>{SLOT_LABELS[index]}</span>{row.candidate.wind_turbines}W / {row.candidate.solar_mw}S{formatBess(row.candidate) ? ` / ${formatBess(row.candidate)}` : ''}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.map((metric) => {
                          const rowValues = slots.map((row) => row.metrics?.[metric.id]);
                          const finite = rowValues.filter(Number.isFinite);
                          const best = finite.length ? (metric.default_direction === 'minimize' ? Math.min(...finite) : Math.max(...finite)) : null;
                          return (
                            <tr key={metric.id}>
                              <td className="scenario-pin-label">{metric.label}<small>{metric.unit}</small></td>
                              {rowValues.map((value, index) => (
                                <td key={index} className={Number.isFinite(value) && value === best && finite.length > 1 ? 'scenario-pin-best' : ''}>{formatValue(value)}</td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <section className="scenario-table-card">
                <div className="scenario-card-head"><div><small>Candidate register</small><h2>{results.length} combinations</h2></div><div className="scenario-actions"><button className="scenario-ghost" onClick={exportWorkbook}>Export XLSX</button></div></div>
                <div className="scenario-table-scroll"><table><thead><tr><th>Rank</th><th>Sizing</th><th>State</th>{metrics.slice(0, 7).map((metric) => <th key={metric.id}>{metric.label}<small>{metric.unit}</small></th>)}<th>Constraint detail</th></tr></thead><tbody>{results.map((row) => {
                  const slotIndex = selectedIds.indexOf(row.candidate.candidate_id);
                  return (
                    <tr key={row.candidate.candidate_id} className={slotIndex >= 0 ? 'selected' : ''} style={slotIndex >= 0 ? { boxShadow: `inset 3px 0 0 ${SLOT_COLORS[slotIndex]}` } : undefined} onClick={() => toggleSlot(row.candidate.candidate_id)}>
                      <td>{row.rank || '—'}{row.pareto && <i>P</i>}</td>
                      <td><strong>{row.candidate.wind_turbines} WTG + {row.candidate.solar_mw} MWp</strong><small>{row.candidate.actual_wind_mw} MW actual wind{formatBess(row.candidate) ? ` · ${formatBess(row.candidate)}` : ''}</small></td>
                      <td><span className={row.feasible ? 'table-ok' : 'table-warn'}>{row.status === 'failed' ? 'Failed' : row.feasible ? 'Feasible' : 'Outside brief'}</span></td>
                      {metrics.slice(0, 7).map((metric) => <td key={metric.id}>{formatValue(row.metrics?.[metric.id])}</td>)}
                      <td className="scenario-violations">{row.error || (row.violations || []).map((item) => item.message).join('; ') || '—'}</td>
                    </tr>
                  );
                })}</tbody></table></div>
              </section>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default ScenarioAnalysis;
